import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { getAxiosInstance } from '../../../axiosInstances/axios';
import { getPrivateAxiosInstance } from '../../../axiosInstances/axiosPrivate';
import { AcademiaResponse } from '../../../types/common';
import { PostalCode, PostalCodeReqBody } from '../../../types/postalCodes';
import { returnQueryConfig } from '../../../utils/commonFunctions';

const usePostalCodesActions = (
  whichAction: 'postalCodeList' | '',
  cityId?: number
) => {
  // query function to fetch all the postal codes declared here
  const getPostalCodeListHandler = useQuery({
    queryKey: ['postal-code-list'],
    queryFn: async (): Promise<AcademiaResponse<Array<PostalCode>>> => {
      const response = await getPrivateAxiosInstance('user').get(
        '/city/postal/code/list/'
      );

      return response.data;
    },

    ...returnQueryConfig(whichAction === 'postalCodeList'),
  });

  const getPostalCodesWithCityIdHandler = useQuery({
    queryKey: ['postal-code-with-city-id', cityId],
    queryFn: async (): Promise<AcademiaResponse<Array<PostalCode>>> => {
      const response = await getAxiosInstance('user').get(
        `/city/postal/codes/with/${cityId}/`
      );

      return response.data;
    },
    ...returnQueryConfig(!!cityId),
  });

  // mutation function to add new postal code declared here
  const addNewPostalCodeHandler = useMutation({
    mutationFn: async ({
      body,
    }: {
      body: Array<PostalCodeReqBody>;
    }): Promise<AcademiaResponse<PostalCode>> => {
      const response = await getPrivateAxiosInstance('user').post(
        '/city/postal/code/create/',
        body
      );

      return response.data;
    },
  });

  const updatePostalCodeByIdHandler = useMutation({
    mutationFn: async ({
      postalCodeId,
      body,
    }: {
      postalCodeId: number;
      body: PostalCodeReqBody;
    }): Promise<AcademiaResponse<PostalCode>> => {
      const response = await getPrivateAxiosInstance('user').patch(
        `/city/postal/code/${postalCodeId}/partial/update/`,
        body
      );

      return response.data;
    },
  });

  // mutation function to delete postal code by id declared here
  const deletePostalCodeByIdHandler = useMutation({
    mutationFn: ({
      postalCodeId,
    }: {
      postalCodeId: number;
    }): Promise<AxiosResponse<AcademiaResponse<null>>> =>
      getPrivateAxiosInstance('user').delete(
        `/city/postal/code/${postalCodeId}/delete/`
      ),
  });

  return {
    getPostalCodeListHandler,
    getPostalCodesWithCityIdHandler,
    addNewPostalCodeHandler,
    updatePostalCodeByIdHandler,
    deletePostalCodeByIdHandler,
  };
};

export default usePostalCodesActions;
